// Lightweight client for Google Routes Preferred API

const ROUTES_ENDPOINT =
	'https://routes.googleapis.com/directions/v2:computeRoutes';

const buildLatLng = (stop) => {
	const lat = Number.parseFloat(stop.lat);
	const lng = Number.parseFloat(stop.lng);
	if (Number.isFinite(lat) && Number.isFinite(lng)) {
		return { latitude: lat, longitude: lng };
	}
	return null;
};

const buildLocation = (stop) => {
	if (stop.placeId) return { placeId: stop.placeId };
	const latLng = buildLatLng(stop);
	// Google Routes expects lat/lng under a nested location key
	return latLng ? { location: { latLng } } : null;
};

/**
 * Call Google Routes Preferred API to compute a route between stops.
 * @param {{ stops: Array<{ lat?: number|string, lng?: number|string, placeId?: string }>, travelMode?: string, extraOptions?: object }} params
 * @returns {Promise<{ totalDuration: number|string, totalDistanceMeters: number, legs: Array<{ duration: number|string, distanceMeters: number, polyline: string }>, polyline: string }>}
 */
export async function computeRoute({
	stops,
	travelMode = 'WALK',
	extraOptions = {},
	apiKey,
}) {
	if (!Array.isArray(stops) || stops.length < 2) {
		throw new Error('At least two stops are required to build a route.');
	}

	const origin = buildLocation(stops[0]);
	const destination = buildLocation(stops[stops.length - 1]);
	const intermediates = stops.slice(1, -1).map(buildLocation).filter(Boolean);

	if (!origin || !destination) {
		throw new Error('Origin and destination need a placeId or valid lat/lng.');
	}

	const key =
		apiKey ||
		(typeof import.meta !== 'undefined' &&
			(import.meta.env?.PUBLIC_GOOGLE_MAPS_API_KEY ||
				import.meta.env?.REACT_APP_GOOGLE_MAPS_API_KEY)) ||
		(typeof process !== 'undefined' &&
			(process.env?.PUBLIC_GOOGLE_MAPS_API_KEY ||
				process.env?.REACT_APP_GOOGLE_MAPS_API_KEY)) ||
		'';
	if (!key) {
		throw new Error(
			'Missing Google Maps API key. Please set PUBLIC_GOOGLE_MAPS_API_KEY or REACT_APP_GOOGLE_MAPS_API_KEY environment variable.'
		);
	}

	const normalizeTravelMode = (mode) => {
		if (mode == null) return 'WALK';
		const raw = `${mode}`.trim().toUpperCase();
		if (!raw) return 'WALK';
		const map = {
			DRIVING: 'DRIVE',
			DRIVE: 'DRIVE',
			WALKING: 'WALK',
			WALK: 'WALK',
			BICYCLING: 'BICYCLE',
			BICYCLE: 'BICYCLE',
			BIKE: 'BICYCLE',
			CYCLING: 'BICYCLE',
			TWO_WHEELER: 'TWO_WHEELER',
			MOTORCYCLE: 'TWO_WHEELER',
			TRANSIT: 'TRANSIT',
		};
		if (map[raw]) return map[raw];
		if (raw.includes('DRIVE')) return 'DRIVE';
		if (raw.includes('WALK')) return 'WALK';
		if (raw.includes('BIKE') || raw.includes('BICYCLE') || raw.includes('CYCLE')) {
			return 'BICYCLE';
		}
		if (raw.includes('TRANSIT')) return 'TRANSIT';
		if (raw.includes('TWO_WHEEL') || raw.includes('MOTOR')) return 'TWO_WHEELER';
		return 'WALK';
	};

	const normalizedMode = normalizeTravelMode(travelMode);

	const body = {
		origin,
		destination,
		intermediates,
		travelMode: normalizedMode,
		// routingPreference can only be set for driving
		routingPreference:
			normalizedMode === 'DRIVE' ? 'TRAFFIC_AWARE_OPTIMAL' : undefined,
		polylineEncoding: 'ENCODED_POLYLINE',
		computeAlternativeRoutes: false,
		units: 'IMPERIAL',
		...(extraOptions || {}),
	};

	// Only set departureTime for driving (traffic-aware) routes
	if (
		normalizedMode === 'DRIVE' &&
		!body.departureTime &&
		body.routingPreference !== 'TRAFFIC_UNAWARE'
	) {
		body.departureTime = new Date(Date.now() + 60_000).toISOString();
	}
	if (normalizedMode !== 'DRIVE') {
		delete body.departureTime;
		delete body.routingPreference;
	}

	const response = await fetch(ROUTES_ENDPOINT, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Goog-Api-Key': key,
			'X-Goog-FieldMask':
				'routes.duration,routes.distanceMeters,routes.legs.duration,routes.legs.distanceMeters,routes.legs.polyline,routes.polyline',
		},
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		const text = await response.text();
		throw new Error(`Routes API error: ${response.status} ${text}`);
	}

	const data = await response.json();
	const route = data.routes?.[0];
	if (!route) {
		throw new Error('No route returned from Google Routes API.');
	}

	const normalize = (r) => ({
		totalDuration: r.duration,
		totalDistanceMeters: r.distanceMeters,
		legs:
			r.legs?.map((leg) => ({
				duration: leg.duration,
				distanceMeters: leg.distanceMeters,
				polyline: leg.polyline?.encodedPolyline,
			})) || [],
		polyline: r.polyline?.encodedPolyline,
	});

	const routes = (data.routes || []).map(normalize);
	const primary = routes[0] || normalize(route);

	return { ...primary, routes };
}

/**
 * Decode Google Encoded Polyline Algorithm Format to [lat, lng] pairs.
 * @param {string} encoded
 * @returns {Array<[number, number]>}
 */
export function decodePolyline(encoded = '') {
	let index = 0;
	const len = encoded.length;
	const path = [];
	let lat = 0;
	let lng = 0;

	while (index < len) {
		let result = 0;
		let shift = 0;
		let b;
		do {
			b = encoded.charCodeAt(index++) - 63;
			result |= (b & 0x1f) << shift;
			shift += 5;
		} while (b >= 0x20);
		const dlat = result & 1 ? ~(result >> 1) : result >> 1;
		lat += dlat;

		result = 0;
		shift = 0;
		do {
			b = encoded.charCodeAt(index++) - 63;
			result |= (b & 0x1f) << shift;
			shift += 5;
		} while (b >= 0x20);
		const dlng = result & 1 ? ~(result >> 1) : result >> 1;
		lng += dlng;

		path.push([lat / 1e5, lng / 1e5]);
	}

	return path;
}
